import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { PropertyUnitsService } from '../../services/property-units.service';
import { ActivatedRoute, Router } from '@angular/router';
import { PROPERTY_RELATIONS_VIEWS } from 'src/app/shared/global/property-relations';
import { RelationOption } from '../../models/property-relations';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AlertBoxComponent } from 'src/app/shared/components/alert-box/alert-box.component';
import { AddUnitFileComponent } from '../add-unit-file/add-unit-file.component';
import { ClientsService } from 'src/app/features/customers/services/clients.service';
import { BuildingTypeService } from 'src/app/core/services/buildingType/building-type.service';
import { CompanyServicesService } from 'src/app/features/companies/services/company-services/company-services.service';
import { ReProjectsService } from 'src/app/features/re-projects/services/re-projects.service';
import { DropdownOption } from 'src/app/shared/Models/dropdown-option.model';

@Component({
  selector: 'add-property-unit',
  templateUrl: './add-property-unit.component.html',
  styleUrls: ['./add-property-unit.component.scss'],
})
export class AddPropertyUnitComponent implements OnInit {
  @Input() embeddedMode: boolean = false;
  @Input() prefilledCustomerId: string | null = null;
  @Input() prefilledServiceId: number | null = null;
  @Output() unitAdded = new EventEmitter<any>();
  @Output() cancelled = new EventEmitter<void>(); 

  submitted: boolean = false;
  errorMsg: boolean = false;
  message: string = '';
  saveDialogRef!: MatDialogRef<AlertBoxComponent>;
  addFileDialogRef!: MatDialogRef<AddUnitFileComponent>;
  relationOptions: RelationOption[] = [];
  propertyFiles: any[] = [];
  fileError: string | null = null;
  customerId: string | null = null;
  customerName: string = '';
  buildingTypes: any[] = [];
  companies: DropdownOption[] = [];
  reProjects: any[] = [];
  // Pagination for companies
  companiesPage: number = 1;
  companiesPageCount: number = 0;
  companiesSearchTerm: string = '';

  propertyUnitFormGroup: FormGroup = new FormGroup({
    client_id: new FormControl<number | null>(null, Validators.required),
    client_name: new FormControl(''),
    property_relations: new FormControl([]),
    custom_name: new FormControl<string | null>(null, Validators.required),
    building_type: new FormControl<number | null>(null, Validators.required),
    surfaces_space: new FormControl<string | null>(null, Validators.required),
    floors_number: new FormControl<number | null>(null),
    age: new FormControl<number | null>(null),
    location: new FormControl<string | null>(null, Validators.required),
    address_area: new FormControl<number | null>(null, Validators.required),
    address_city: new FormControl<number | null>(null, Validators.required),
    district: new FormControl<number | null>(null, Validators.required),
    address_street: new FormControl<string | null>(''),
    short_address: new FormControl<string | null>(''),
    address_building_code: new FormControl<string | null>(''),
    landmark: new FormControl<string | null>(''),
    client_representative: new FormControl<string | null>(null),
    developer_name: new FormControl<string | null>(null),
    project_name: new FormControl<string | null>(null),
  });

  constructor(
    private propertyUnitService: PropertyUnitsService,
    private clientsService: ClientsService,
    private buildingTypeService: BuildingTypeService,
    private companyService: CompanyServicesService,
    private reProjectsService: ReProjectsService,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
  ) {}

  ngOnInit() {
    if (this.prefilledCustomerId) {
      this.customerId = this.prefilledCustomerId;
      this.propertyUnitFormGroup.patchValue({
        client_id: Number(this.customerId),
      });
    } else {
      this.route.queryParams.subscribe((params) => {
        if (params['customer_id']) {
          this.customerId = params['customer_id'];
          if (this.customerId) {
            this.loadCustomerInfo(this.customerId);
          }
        }
      });
    }

    this.getPropertyRelations();
    this.getBuildingTypes();
    this.loadCompanies();

    this.propertyUnitFormGroup.get('short_address')?.clearValidators();
    this.propertyUnitFormGroup.get('short_address')?.updateValueAndValidity();

    this.propertyUnitFormGroup.statusChanges.subscribe((status) => {
      if (status === 'VALID' && this.submitted) {
        this.errorMsg = false;
      }
    });
  }

  loadCustomerInfo(customerId: string) {
    this.clientsService.addClients(Number(customerId)).subscribe({
      next: (client) => {
        this.customerName = client.full_name || client.name || '';
        this.propertyUnitFormGroup.patchValue({
          client_id: client.id,
          client_name: this.customerName,
        });
      },
      error: (error) => {
        console.error('Error loading customer info:', error);
      },
    });
  }

  getBuildingTypes() {
    this.buildingTypeService.getBuildingTypes().subscribe((data) => {
      this.buildingTypes = data;
    });
  }

  getPropertyRelations() {
    this.propertyUnitService
      .getPropertyRelations(PROPERTY_RELATIONS_VIEWS.UNIT)
      .subscribe((relations) => {
        this.relationOptions = relations.data || [];
      });
  }

  isSelected(relationId: string): boolean {
    const selectedRelations =
      this.propertyUnitFormGroup.get('property_relations')?.value || [];
    return selectedRelations.includes(relationId);
  }

  toggleRelation(relationId: string) {
    const selectedRelations =
      this.propertyUnitFormGroup.get('property_relations')?.value || [];
    const index = selectedRelations.indexOf(relationId);

    if (index > -1) {
      selectedRelations.splice(index, 1);
    } else {
      selectedRelations.push(relationId);
    }

    this.propertyUnitFormGroup.patchValue({
      property_relations: [...selectedRelations],
    });
  }

  selectRepresentative(relationId: string) {
    this.propertyUnitFormGroup.patchValue({
      client_representative: relationId,
    });
  }

  save() {
    this.submitted = true;
    const isFormValid = Object.entries(this.propertyUnitFormGroup.controls)
      .filter(([name]) => name !== 'short_address')
      .every(([, control]) => control.valid);

    if (!isFormValid) {
      this.errorMsg = true;
      this.message = 'برجاء إدخال الحقول المطلوبة';
      return;
    }

    const formValue = this.propertyUnitFormGroup.value;
    const unitData: any = {
      client_id: this.customerId,
      building_type: formValue.building_type,
      age: formValue.age,
      floors_number: formValue.floors_number || null,
      location: formValue.location || null,
      landmark: formValue.landmark || null,
      short_address: formValue.short_address || null,
      address_building_code: formValue.address_building_code || null,
      surfaces_space: formValue.surfaces_space,
      thumbnail: null,
      custom_name: formValue.custom_name,
      property_relations: formValue.property_relations || [],
      client_representative: formValue.client_representative || null,
      stakeholder: formValue.client_id,
      address_area: formValue.address_area,
      address_city: formValue.address_city,
      district: formValue.district,
      address_unit_code: formValue.address_unit_code || null,
      address_street: formValue.address_street || null,
      linked_real_estate_project: formValue.project_name || null,
    };

    if (this.customerId) {
      this.propertyUnitService
        .addCustomerUnit(Number(this.customerId), unitData)
        .subscribe({
          next: (response) => {
            if (this.embeddedMode) {
              this.unitAdded.emit({
                id: response.id || response.data?.id,
                data: response.data || response,
              });
            } else {
              this.saveDialogRef = this.dialog.open(AlertBoxComponent, {
                data: {
                  message: 'تم إضافة الوحدة العقارية بنجاح',
                  imgSuccess: true,
                },
              });

              this.saveDialogRef.afterClosed().subscribe(() => {
                // Navigate to the unit details page
                this.router.navigate([
                  '/pages/property-units',
                  response.id || response.data?.id,
                ]);
              });
            }
          },
          error: (error) => {
            this.errorMsg = true;
            this.message =
              error?.error?.message || 'حدث خطأ أثناء إضافة الوحدة العقارية';
            console.error('Error creating property unit:', error);
          },
        });
    } else {
      // Fallback to regular create if no customerId
      const formData = new FormData();

      // Append all form fields to FormData
      Object.keys(unitData).forEach((key) => {
        if (unitData[key] !== null && unitData[key] !== undefined) {
          if (key === 'property_relations' && Array.isArray(unitData[key])) {
            unitData[key].forEach((relationId: number) => {
              formData.append('property_relations', relationId.toString());
            });
          } else {
            formData.append(key, unitData[key].toString());
          }
        }
      });

      // Append files
      this.propertyFiles.forEach((file) => {
        if (file.file) {
          formData.append('files', file.file);
          formData.append('file_type_names', file.file_type_name);
        }
      });

      this.propertyUnitService.createPropertyUnit(formData).subscribe({
        next: (response) => {
          this.saveDialogRef = this.dialog.open(AlertBoxComponent, {
            data: {
              message: 'تم إضافة الوحدة العقارية بنجاح',
              imgSuccess: true,
            },
          });

          this.saveDialogRef.afterClosed().subscribe(() => {
            // Navigate to the unit details page
            this.router.navigate(['/pages/property-units', response.id]);
          });
        },
        error: (error) => {
          this.errorMsg = true;
          this.message =
            error?.error?.message || 'حدث خطأ أثناء إضافة الوحدة العقارية';
          console.error('Error creating property unit:', error);
        },
      });
    }
  }

  closeError() {
    this.errorMsg = false;
  }

  goBack() {
    if (this.embeddedMode) {
      this.cancelled.emit();
    } else {
      this.router.navigate(['/pages/property-units']);
    }
  }

  loadCompanies(searchTerm: string = '') {
    const params: any = { page: this.companiesPage, page_size: 20 };
    if (searchTerm) {
      params.query = searchTerm;
    }

    this.companyService.getCompaniesList(params).subscribe(
      (response: any) => {
        if (this.companiesPage === 1) {
          this.companies = response?.results || [];
        } else {
          this.companies = [...this.companies, ...(response?.results || [])];
        }
        this.companiesPageCount = response?.pages_count || 0;
      },
      (error) => console.error('Error loading companies:', error),
    );
  }

  onCompanyScroll() {
    if (this.companiesPage < this.companiesPageCount) {
      this.companiesPage++;
      this.loadCompanies(this.companiesSearchTerm);
    }
  }

  onCompanySearch(event: any) {
    this.companiesSearchTerm = event.term || '';
    this.companiesPage = 1;
    this.loadCompanies(this.companiesSearchTerm);
  }

  companyChanged(event: any) {
    if (event) {
      this.getREProjects(event);
      // Clear project selection when company changes
      this.propertyUnitFormGroup.patchValue({
        project_name: null,
      });
    } else {
      this.reProjects = [];
    }
  }

  getREProjects(companyId: number) {
    this.reProjectsService.getCompanyREProjects(companyId).subscribe(
      (data) => {
        this.reProjects = data.data || data;
      },
      (error) => {
        console.error('Error loading RE projects:', error);
        this.reProjects = [];
      },
    );
  }
}
